import { useForm } from '@tanstack/react-form';
import { Button } from '@sara/ui/components/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { Input } from '@sara/ui/components/input';
import { createDomainFormSchema, type CreateDomainFormValues } from './schemas';

function deriveApiHostnamePreview(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;
  const dot = trimmed.indexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return null;
  const first = trimmed.slice(0, dot);
  const rest = trimmed.slice(dot + 1);
  return `${first}-api.${rest}`;
}

interface CreateDomainFormProps {
  onSubmit: (values: CreateDomainFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
}

export function CreateDomainForm({ onSubmit, onCancel, isPending, error }: CreateDomainFormProps) {
  const form = useForm({
    defaultValues: { hostname: '' } as CreateDomainFormValues,
    validators: { onSubmit: createDomainFormSchema },
    onSubmit: ({ value }) => onSubmit({ hostname: value.hostname.trim().toLowerCase() }),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-5"
      autoComplete="off"
    >
      <form.Field name="hostname">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
                Interview hostname
              </FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  placeholder="interviews.acme.com"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                {(() => {
                  const preview = deriveApiHostnamePreview(field.state.value);
                  return preview ? (
                    <p className="text-xs text-muted-foreground">
                      We&rsquo;ll automatically pair this with{' '}
                      <span className="font-mono text-foreground/90">{preview}</span> for the API.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      We&rsquo;ll automatically pair this with a matching API hostname (e.g.{' '}
                      <span className="font-mono text-foreground/90">interviews-api.acme.com</span>
                      ).
                    </p>
                  );
                })()}
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/8 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="tinted"
          shape="pill"
          size="sm"
          className="gap-1.5"
          disabled={isPending}
        >
          {isPending ? 'Adding…' : 'Add domain'}
        </Button>
      </div>
    </form>
  );
}
